import { gsap } from 'gsap';

const selectors = {
  root: '.social-carousel',
  fan: '[data-fan]',
  itemsJson: '[data-social-carousel-items]',
  card: '.social-carousel__card',
  media: '[data-media]',
  chip: '[data-chip]',
};

const SWIPE_THRESHOLD = 50;
const ANIMATION_DURATION = 0.55;
const DRAG_PREVIEW_DISTANCE = 160;
const DRAG_PREVIEW_CAP = 0.42;
const TAP_TOLERANCE = 8;

const POSITION_MAP = {
  center: { x: 0, y: 0, rotate: 0.6, scale: 1, opacity: 1, zIndex: 8 },
  'left-1': { x: -148, y: 18, rotate: -13.8, scale: 0.9, opacity: 1, zIndex: 7 },
  'left-2': { x: -292, y: 72, rotate: -28.2, scale: 0.9, opacity: 1, zIndex: 6 },
  'left-3': { x: -422, y: 132, rotate: -42.6, scale: 0.9, opacity: 1, zIndex: 5 },
  'right-1': { x: 148, y: 18, rotate: 15, scale: 0.9, opacity: 1, zIndex: 7 },
  'right-2': { x: 292, y: 72, rotate: 29.4, scale: 0.9, opacity: 1, zIndex: 6 },
  'right-3': { x: 422, y: 132, rotate: 43.8, scale: 0.9, opacity: 1, zIndex: 5 },
  'buffer-left': { x: -560, y: 180, rotate: -58.2, scale: 0.9, opacity: 0, zIndex: 1 },
  'buffer-right': { x: 560, y: 180, rotate: 58.2, scale: 0.9, opacity: 0, zIndex: 1 },
};

const LOOP_POSITIONS = {
  7: ['buffer-left', 'left-2', 'left-1', 'center', 'right-1', 'right-2', 'buffer-right'],
  9: ['buffer-left', 'left-3', 'left-2', 'left-1', 'center', 'right-1', 'right-2', 'right-3', 'buffer-right'],
};

const VISUAL_POSITIONS = ['left-3', 'left-2', 'left-1', 'center', 'right-1', 'right-2', 'right-3'];

function buildMediaMarkup(item) {
  if (item.type === 'video' && item.video_url) {
    const mimeType = item.video_mime_type || 'video/mp4';
    return `
      <video loop muted playsinline preload="metadata">
        <source src="${item.video_url}" type="${mimeType}">
      </video>
    `;
  }

  return item.image_html || '';
}

function buildChipMarkup(platform, platformIconHtml) {
  if (!platform) {
    return '';
  }

  const iconMarkup = platformIconHtml || '';

  return `${iconMarkup}<span class="social-carousel__chip-label">${platform}</span>`;
}

function getPositionStyle(position) {
  return POSITION_MAP[position] || POSITION_MAP.center; 
}

function getLoopWindowIndices(itemCount, slotCount, currentIndex) {
  const centerSlotIndex = Math.floor(slotCount / 2);

  return Array.from({ length: slotCount }, (_, slotIndex) => {
    const offset = slotIndex - centerSlotIndex;
    return (currentIndex + offset + itemCount) % itemCount;
  });
}

function getNonLoopPositions(itemCount, currentIndex) {
  const centerPositionIndex = 3 - currentIndex;

  return Array.from({ length: itemCount }, (_, slotIndex) => {
    const visualIndex = centerPositionIndex + slotIndex;
    return VISUAL_POSITIONS[visualIndex] || 'buffer-right';
  });
}

function setTransform(card, position, duration = 0) {
  const style = getPositionStyle(position);

  applyStyle(card, style, duration);
  card.dataset.position = position;
}

function applyStyle(card, style, duration = 0) {

  card.style.zIndex = String(style.zIndex);

  gsap.to(card, {
    xPercent: -50,
    x: style.x,
    y: style.y,
    rotation: style.rotate,
    scale: style.scale,
    autoAlpha: style.opacity,
    duration,
    ease: duration > 0 ? 'power2.inOut' : 'none',
    overwrite: true,
  });
}

function getInterpolatedStyle(fromPosition, toPosition, progress) {
  const fromStyle = getPositionStyle(fromPosition);
  const toStyle = getPositionStyle(toPosition);

  return {
    x: fromStyle.x + ((toStyle.x - fromStyle.x) * progress),
    y: fromStyle.y + ((toStyle.y - fromStyle.y) * progress),
    rotate: fromStyle.rotate + ((toStyle.rotate - fromStyle.rotate) * progress),
    scale: fromStyle.scale + ((toStyle.scale - fromStyle.scale) * progress),
    opacity: fromStyle.opacity + ((toStyle.opacity - fromStyle.opacity) * progress),
    zIndex: progress >= 0.5 ? toStyle.zIndex : fromStyle.zIndex,
  };
}

function getRubberbandProgress(deltaX) {
  const distance = Math.abs(deltaX);

  if (distance === 0) {
    return 0;
  }

  const normalizedDistance = distance / DRAG_PREVIEW_DISTANCE;

  return DRAG_PREVIEW_CAP * (1 - Math.exp(-normalizedDistance / DRAG_PREVIEW_CAP));
}

function syncVideos(cards) {
  cards.forEach((card) => {
    const video = card.querySelector('video');

    if (!video) {
      return;
    }

    if (card.classList.contains('is-active')) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
      return;
    }

    video.pause();
    video.currentTime = 0;
  });
}

function setCardContent(card, item, itemIndex) {
  const mediaNode = card.querySelector(selectors.media);
  const chipNode = card.querySelector(selectors.chip);
  const nextItemIndex = String(itemIndex);
  const mediaChanged =
    card.dataset.itemIndex !== nextItemIndex ||
    card.dataset.type !== item.type ||
    card.dataset.url !== item.url ||
    card.dataset.videoUrl !== (item.video_url || '');

  card.dataset.itemIndex = nextItemIndex;
  card.dataset.type = item.type;
  card.dataset.url = item.url;
  card.dataset.platform = item.platform || '';
  card.dataset.platformIcon = item.platform_icon || '';
  card.dataset.platformIconHtml = item.platform_icon_html || '';
  card.dataset.videoUrl = item.video_url || '';
  card.dataset.videoMimeType = item.video_mime_type || '';

  if (mediaNode && mediaChanged) {
    mediaNode.innerHTML = buildMediaMarkup(item);
  }

  if (chipNode) {
    chipNode.innerHTML = buildChipMarkup(item.platform || '', item.platform_icon_html || '');
  }
}

function setCardAppearance(card, position, duration = 0) {
  const isActive = position === 'center';
  const chipNode = card.querySelector(selectors.chip);

  card.classList.toggle('is-active', isActive);
  card.classList.toggle('is-idle', !isActive);

  if (chipNode) {
    chipNode.classList.toggle('is-visible', Boolean(isActive && card.dataset.platform));
  }

  setTransform(card, position, duration);
}

function initCarousel(carousel) {
  const fan = carousel.querySelector(selectors.fan);
  const itemsNode = carousel.querySelector(selectors.itemsJson);
  const cards = Array.from(carousel.querySelectorAll(selectors.card));

  if (!fan || !itemsNode || cards.length === 0) {
    return;
  }

  let items;

  try {
    items = JSON.parse(itemsNode.textContent || '[]');
  } catch (error) {
    console.error('Invalid social carousel JSON payload', error);
    return;
  }

  const itemCount = Number.parseInt(carousel.dataset.n || '0', 10);
  const slotCount = Number.parseInt(carousel.dataset.slotCount || '0', 10);
  const autoplayEnabled = carousel.dataset.autoplay === '1';
  const autoplayInterval = Number.parseInt(carousel.dataset.autoplayInterval || '4000', 10);
  const looping = itemCount >= 5;
  const slotPositions = looping
    ? LOOP_POSITIONS[slotCount] || LOOP_POSITIONS[9]
    : getNonLoopPositions(itemCount, 0);

  let currentIndex = 0;
  let autoplayTimer = null;
  let animationTimeoutId = null;
  let pointerStartX = 0;
  let pointerCurrentX = 0;
  let isPointerDown = false;
  let isAnimating = false;
  let slotNodes = cards;
  let pendingAnimation = null;

  cards.forEach((card) => {
    gsap.set(card, {
      xPercent: -50,
      transformOrigin: 'center center',
    });
  });

  const renderLoopState = (duration = 0) => {
    const windowIndices = getLoopWindowIndices(itemCount, slotCount, currentIndex);

    slotNodes.forEach((card, index) => {
      const itemIndex = windowIndices[index];
      const item = items[itemIndex];

      setCardContent(card, item, itemIndex);
      setCardAppearance(card, slotPositions[index], duration);
    });

    syncVideos(slotNodes);
  };

  const renderNonLoopState = (duration = 0) => {
    const positions = getNonLoopPositions(itemCount, currentIndex);

    cards.forEach((card, index) => {
      setCardContent(card, items[index], index);
      setCardAppearance(card, positions[index] || 'buffer-right', duration);
    });

    syncVideos(cards);
  };

  const renderState = (duration = 0) => {
    if (looping) {
      renderLoopState(duration);
      return;
    }

    renderNonLoopState(duration);
  };

  const canNavigate = (direction) => {
    if (looping) {
      return true;
    }

    if (direction === 'left') {
      return currentIndex < itemCount - 1;
    }

    return currentIndex > 0;
  };

  const applyLoopPreview = (direction, progress) => {
    const lastIndex = slotNodes.length - 1;

    slotNodes.forEach((card, index) => {
      const sourcePosition = slotPositions[index];
      const targetIndex = direction === 'left' ? Math.max(index - 1, 0) : Math.min(index + 1, lastIndex);
      const targetPosition = slotPositions[targetIndex];
      const previewStyle = getInterpolatedStyle(sourcePosition, targetPosition, progress);

      applyStyle(card, previewStyle);
      card.dataset.position = sourcePosition;
    });
  };

  const applyNonLoopPreview = (direction, progress) => {
    const currentPositions = getNonLoopPositions(itemCount, currentIndex);
    const previewIndex = direction === 'left' ? currentIndex + 1 : currentIndex - 1;
    const nextPositions = getNonLoopPositions(itemCount, previewIndex);

    cards.forEach((card, index) => {
      const sourcePosition = currentPositions[index] || 'buffer-right';
      const targetPosition = nextPositions[index] || 'buffer-right';
      const previewStyle = getInterpolatedStyle(sourcePosition, targetPosition, progress);

      applyStyle(card, previewStyle);
      card.dataset.position = sourcePosition;
    });
  };

  const applyDragPreview = (deltaX) => {
    if (deltaX === 0) {
      renderState();
      return;
    }

    const direction = deltaX < 0 ? 'left' : 'right';

    if (!canNavigate(direction)) {
      renderState();
      return;
    }

    const progress = getRubberbandProgress(deltaX);

    if (looping) {
      applyLoopPreview(direction, progress);
      return;
    }

    applyNonLoopPreview(direction, progress);
  };

  const stopAutoplay = () => {
    if (autoplayTimer) {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const startAutoplay = () => {
    stopAutoplay();

    if (!autoplayEnabled || !looping) {
      return;
    }

    autoplayTimer = window.setInterval(() => {
      navigate('left');
    }, autoplayInterval);
  };

  const clearAnimationTimeout = () => {
    if (animationTimeoutId) {
      window.clearTimeout(animationTimeoutId);
      animationTimeoutId = null;
    }
  };

  const completePendingAnimation = () => {
    if (!pendingAnimation) {
      return;
    }

    clearAnimationTimeout();
    gsap.killTweensOf(looping ? slotNodes : cards);

    if (pendingAnimation.looping) {
      const lastIndex = slotNodes.length - 1;

      if (pendingAnimation.direction === 'left') {
        slotNodes = [...slotNodes.slice(1), slotNodes[0]];
      } else {
        slotNodes = [slotNodes[lastIndex], ...slotNodes.slice(0, lastIndex)];
      }

      currentIndex = pendingAnimation.nextIndex;
      renderLoopState();
    } else {
      currentIndex = pendingAnimation.nextIndex;
      renderNonLoopState();
    }

    pendingAnimation = null;
    isAnimating = false;
  };

  const navigate = (direction) => {
    if (isAnimating) {
      return;
    }

    const delta = direction === 'left' ? 1 : -1;
    const nextIndex = looping
      ? (currentIndex + delta + itemCount) % itemCount
      : currentIndex + delta;

    if (!looping && (nextIndex < 0 || nextIndex >= itemCount)) {
      return;
    }

    isAnimating = true;
    stopAutoplay();
    pendingAnimation = {
      looping,
      direction,
      nextIndex,
    };

    if (looping) {
      const lastIndex = slotNodes.length - 1;

      slotNodes.forEach((card, index) => {
        const targetIndex = direction === 'left' ? Math.max(index - 1, 0) : Math.min(index + 1, lastIndex);
        setCardAppearance(card, slotPositions[targetIndex], ANIMATION_DURATION);
      });

      animationTimeoutId = window.setTimeout(() => {
        completePendingAnimation();
        startAutoplay();
      }, (ANIMATION_DURATION * 1000) + 20);

      return;
    } else {
      currentIndex = nextIndex;
      renderNonLoopState(ANIMATION_DURATION);
    }

    animationTimeoutId = window.setTimeout(() => {
      syncVideos(cards);
      pendingAnimation = null;
      isAnimating = false;
      clearAnimationTimeout();
      startAutoplay();
    }, (ANIMATION_DURATION * 1000) + 20);
  };

  fan.addEventListener('pointerdown', (event) => {
    if (isAnimating) {
      completePendingAnimation();
    }

    isPointerDown = true;
    pointerStartX = event.clientX;
    pointerCurrentX = event.clientX;
    stopAutoplay();

    if (typeof fan.setPointerCapture === 'function') {
      fan.setPointerCapture(event.pointerId);
    }
  });

  fan.addEventListener('pointermove', (event) => {
    if (!isPointerDown || isAnimating) {
      return;
    }

    pointerCurrentX = event.clientX;
    applyDragPreview(pointerCurrentX - pointerStartX);
  });

  const finishPointerInteraction = (event) => {
    if (!isPointerDown) {
      return;
    }

    isPointerDown = false;

    if (typeof fan.releasePointerCapture === 'function' && fan.hasPointerCapture?.(event.pointerId)) {
      fan.releasePointerCapture(event.pointerId);
    }

    const deltaX = pointerCurrentX - pointerStartX;
    const distance = Math.abs(deltaX);

    if (distance >= SWIPE_THRESHOLD) {
      navigate(deltaX < 0 ? 'left' : 'right');
      return;
    }

    renderState(0.2);

    if (distance > TAP_TOLERANCE) {
      startAutoplay();
      return;
    }

    const activeCard = (looping ? slotNodes : cards).find((card) => card.classList.contains('is-active'));

    if (!activeCard || !activeCard.dataset.url) {
      startAutoplay();
      return;
    }

    const rect = activeCard.getBoundingClientRect();
    const insideActiveCard =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (insideActiveCard) {
      window.open(activeCard.dataset.url, '_blank', 'noopener');
      return;
    }

    startAutoplay();
  };

  fan.addEventListener('pointerup', finishPointerInteraction);
  fan.addEventListener('pointercancel', (event) => {
    finishPointerInteraction(event);
  });

  renderState();
  startAutoplay();
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll(selectors.root).forEach(initCarousel);
});